import {Alert} from 'antd';
import {useEffect, useMemo, useState} from 'react';

import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {
    useAvailableCatalogs,
    useCatalogConfigForCompanyForm,
    useCatalogInfo,
} from '../../../../../../provider/catalogs/catalogs-hook';
import {CatalogConfigType} from '../../../../../../provider/catalogs/catalogs-type';
import {LocaleWithCatalogName} from '../../../../../../provider/locale/facebook-localization/locale-with-catalog-name';
import {Locale} from '../../../../../../provider/locale/locale';
import {FILE_SIZE_BYTES_FOR_GOOGLE} from '../../../../../../service/company/company-const';
import {CompanyKeyEnum} from '../../../../../../service/company-v2/company-const';
import {CompanyFormType} from '../../../../../../service/company-v2/company-type';
import {Form, FormInstance} from '../../../../../../typings/antd';
import {ProvidersIdsEnum} from '../../../../../../util/type';
import {CatalogConfigWithIndexType, CatalogList} from '../../catalog-list/catalog-list';
import {getErrorTabIndex} from '../../catalog-list/catalog-list-helper';
import {CatalogTab} from '../../catalog-list/catalog-tab';
import * as infoForImageStyles from '../../info-for-image/info-for-image.scss';

import {GalleryFormItem} from './gallery-form-item/gallery-form-item';
import * as styles from './gallery-widget.scss';

type PropsType = {
    name: string;
    initialCatalogs: Array<number>;
    companyId: number;
};

export function GalleryWidget(props: PropsType): JSX.Element {
    const {name, initialCatalogs, companyId} = props;

    const [galleryError, setGalleryError] = useState<Array<string> | null>(null);
    const [extraGalleryError, setExtraGalleryError] = useState<Array<string> | null>(null);
    const [uploadedImagesWithWarnings, setUploadedImagesWithWarnings] = useState<Set<string>>(new Set());
    const [isMainGalleryAlertClosed, setIsMainGalleryAlertClosed] = useState<boolean>(false);
    const [isExtraGalleryAlertClosed, setIsExtraGalleryAlertClosed] = useState<boolean>(false);
    const catalogConfigHook = useCatalogConfigForCompanyForm();
    const {getCatalogName} = useCatalogInfo();
    const {isCatalogAvailable} = useAvailableCatalogs();

    const filteredCatalogNames = useMemo(() => {
        if (!catalogConfigHook.result) {
            return null;
        }

        const catalogs = catalogConfigHook.result
            .filter((catalog) => isCatalogAvailable(catalog.catalogId) && catalog.canRewriteGallery)
            .map((catalog) => getCatalogName(catalog.catalogId))
            .filter((catalogName) => catalogName.length > 0);

        return catalogs.length > 0 ? catalogs.join(', ') : null;
    }, [catalogConfigHook.result, getCatalogName, isCatalogAvailable]);

    const galleryImages = Form.useWatch<CompanyKeyEnum.Gallery, FormInstance<CompanyFormType>>(CompanyKeyEnum.Gallery);
    const extraGalleryImages = Form.useWatch<CompanyKeyEnum.ExtraGallery, FormInstance<CompanyFormType>>(
        CompanyKeyEnum.ExtraGallery
    );
    const galleryGoogleExtraFields = Form.useWatch<CompanyKeyEnum.ExtraGallery, FormInstance<CompanyFormType>>(
        CompanyKeyEnum.ExtraGallery
    )?.some((catalog) => catalog.catalogId === ProvidersIdsEnum.google);

    const isShowGalleryAlert =
        (galleryImages || []).some(
            (item) =>
                !('thumbnail' in item) &&
                !item?.errorMessage?.length &&
                (item?.size ? item.size > FILE_SIZE_BYTES_FOR_GOOGLE : false)
        ) && !galleryGoogleExtraFields;

    function addUploadedImagesWithWarnings(temporaryIds: Array<string>) {
        setUploadedImagesWithWarnings((previous) => {
            const updated = new Set(previous);

            temporaryIds.forEach((temporaryId) => updated.add(temporaryId));

            return updated;
        });
    }

    useEffect(() => {
        const existingTemporaryIds = new Set(
            (extraGalleryImages || []).flatMap((catalogImage) =>
                (catalogImage.gallery || []).map((image) => image.tempId).filter(Boolean)
            )
        );

        setUploadedImagesWithWarnings((previous) => {
            const updated = new Set([...previous].filter((temporaryId) => existingTemporaryIds.has(temporaryId)));

            return updated.size !== previous.size ? updated : previous;
        });
    }, [extraGalleryImages]);

    return (
        <>
            <Form.Item<CompanyFormType>
                help={galleryError ? <div className={styles.error}>{galleryError}</div> : null}
                name={CompanyKeyEnum.Gallery}
                validateStatus={galleryError ? 'error' : undefined} // eslint-disable-line no-undefined
            >
                <GalleryFormItem companyId={companyId} setGalleryError={setGalleryError} />
            </Form.Item>
            {isShowGalleryAlert && (
                <Alert
                    className={styles.Gallery_googleAlert}
                    closable
                    description={
                        <LocaleWithCatalogName
                            catalogId={ProvidersIdsEnum.google}
                            namePlaceholder="Google"
                            stringKey="COMPANY_FORM__GALLERY__GOOGLE_CATALOG__ALERT"
                        />
                    }
                    showIcon
                    type="warning"
                />
            )}
            {(galleryImages || [])?.some((image) => image?.warnings?.length) && !isMainGalleryAlertClosed && (
                <Alert
                    className={styles.Gallery_googleAlert}
                    closable
                    description={<Locale stringKey="COMPANY_FORM__GALLERY__CHANGES_IMAGES_ALERT__DESCRIPTION" />}
                    message={<Locale stringKey="COMPANY_FORM__GALLERY__CHANGES_IMAGES_ALERT__TITLE" />}
                    onClose={() => setIsMainGalleryAlertClosed(true)}
                    showIcon
                    type="warning"
                />
            )}
            <Form.Item<CompanyFormType> dependencies={[CompanyKeyEnum.Gallery]} noStyle>
                {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                {({getFieldValue, setFieldsValue, getFieldsError}) => (
                    <CatalogList
                        activeTabIndex={getErrorTabIndex(getFieldsError, CompanyKeyEnum.ExtraGallery)}
                        disabled={getFieldValue(CompanyKeyEnum.Gallery).length === 0}
                        initialCatalogs={initialCatalogs}
                        isCatalogVisible={({canRewriteGallery}) => canRewriteGallery}
                        onAddCatalogs={(catalogs: Array<CatalogConfigType>) => {
                            setFieldsValue({
                                extraGallery: [
                                    ...getFieldValue(CompanyKeyEnum.ExtraGallery),
                                    ...catalogs.map(({catalogId}) => ({
                                        catalogId,
                                        gallery: getFieldValue(CompanyKeyEnum.Gallery).filter((item) => {
                                            if (item.video) {
                                                return false;
                                            }

                                            return catalogId === ProvidersIdsEnum.google
                                                ? (item?.size || 0) < FILE_SIZE_BYTES_FOR_GOOGLE
                                                : true;
                                        }),
                                    })),
                                ],
                            });
                        }}
                        onCatalogAvailabilityChange={(availableCatalogIds: Array<number>) => {
                            setFieldsValue({
                                extraGallery: getFieldValue(CompanyKeyEnum.ExtraGallery).filter(({catalogId}) =>
                                    availableCatalogIds.includes(catalogId)
                                ),
                            });
                        }}
                        renderCatalog={(catalog: CatalogConfigWithIndexType, {handleRemoveCatalog}) => {
                            const hasUploadedImagesWithWarnings = (extraGalleryImages || [])?.some(
                                (catalogImage) =>
                                    catalogImage.catalogId === catalog.catalogId &&
                                    catalogImage.gallery?.some(
                                        (image) => image?.tempId && uploadedImagesWithWarnings.has(image.tempId)
                                    )
                            );

                            return (
                                <Form.List name={CompanyKeyEnum.ExtraGallery}>
                                    {(_fields, {remove}) => (
                                        <CatalogTab
                                            catalog={catalog}
                                            modalFieldName={name}
                                            onRemove={(index: number) => {
                                                remove(index);
                                                handleRemoveCatalog(catalog.catalogId);
                                            }}
                                        >
                                            <>
                                                <Form.Item
                                                    help={
                                                        extraGalleryError ? (
                                                            <div className={styles.error}>{extraGalleryError}</div>
                                                        ) : null
                                                    }
                                                    name={[catalog.index, 'gallery']}
                                                    validateStatus={extraGalleryError ? 'error' : undefined} // eslint-disable-line no-undefined
                                                >
                                                    <GalleryFormItem
                                                        catalogId={catalog.catalogId}
                                                        companyId={companyId}
                                                        maxSize={
                                                            catalog.catalogId === ProvidersIdsEnum.google
                                                                ? 'COMPANY_FORM__GALLERY__HINT__MAX_SIZE__GOOGLE'
                                                                : null
                                                        }
                                                        onImageUpload={addUploadedImagesWithWarnings}
                                                        setGalleryError={setExtraGalleryError}
                                                    />
                                                </Form.Item>
                                                {hasUploadedImagesWithWarnings && !isExtraGalleryAlertClosed && (
                                                    <Alert
                                                        className={styles.Gallery_googleAlert}
                                                        closable
                                                        description={
                                                            <Locale stringKey="COMPANY_FORM__GALLERY__CHANGES_IMAGES_ALERT__DESCRIPTION" />
                                                        }
                                                        message={
                                                            <Locale stringKey="COMPANY_FORM__GALLERY__CHANGES_IMAGES_ALERT__TITLE" />
                                                        }
                                                        onClose={() => setIsExtraGalleryAlertClosed(true)}
                                                        showIcon
                                                        type="warning"
                                                    />
                                                )}
                                            </>
                                        </CatalogTab>
                                    )}
                                </Form.List>
                            );
                        }}
                        title={
                            <AdditionalInfo
                                leftNode={<Locale stringKey="COMPANY_FORM__GALLERY__CATALOGS_HEADER" />}
                                title={<Locale stringKey="IMAGES__PHOTO_GALLERY" />}
                            >
                                {filteredCatalogNames && (
                                    <div>
                                        <span className={infoForImageStyles.AdditionalInfo_title}>
                                            <Locale stringKey="COMPANY_FORM__IMAGE__SENT_TO_CATALOGS__TITLE" />
                                        </span>

                                        {filteredCatalogNames}
                                    </div>
                                )}
                            </AdditionalInfo>
                        }
                    />
                )}
            </Form.Item>
        </>
    );
}
